import type { QueryClient } from '@tanstack/react-query';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { GET_SEQUENCE_INDEX_STATUS_QUERY_KEY } from '@/state/queries/player/use-get-sequence-index-status';

export type SequenceIndexChangePayload = {
  entityId: string;
  entityType: string;
  index: number;
  mediaId?: string;
  frameCount?: number;
  /** Server epoch when this step was entered. */
  changedAt?: number;
  /** Server epoch when this step may advance. */
  holdUntil?: number;
  /** Server epoch when the event was sent. */
  emittedAt?: number;
  /** Start stamp for each embedded video still showing on this step. */
  videoStartedAt?: Record<string, number>;
};

export function applyServerSequenceIndexChange(
  data: SequenceIndexChangePayload,
  options?: { entityRef?: string; queryClient?: QueryClient; fallbackItemIds?: string[] },
): void {
  const itemIds: string[] = [];
  const dataItemId = data.mediaId
    ? data.mediaId.startsWith('media:')
      ? data.mediaId
      : `media:${data.mediaId}`
    : undefined;

  if (dataItemId) {
    // Scoped updates must not stamp every live sequenced item (e.g. agent PPT → on-air PDF).
    itemIds.push(dataItemId);
  } else {
    for (const id of options?.fallbackItemIds ?? []) {
      if (id && !itemIds.includes(id)) {
        itemIds.push(id);
      }
    }
  }

  for (const itemId of itemIds) {
    const liveStore = useLiveSequenceIndexStore.getState();
    // Live sequence-index-change is the clicker path (Control and player). Durable GET /
    // perform-control mediaSequenceIndex still respects optimistic hold.
    liveStore.setIndex(itemId, data.index, { confirm: true });
    if (data.frameCount != null && data.frameCount > 0) {
      liveStore.reportTotalPages(itemId, data.frameCount);
    }
    if (typeof data.emittedAt === 'number' && data.emittedAt > 0) {
      liveStore.setServerClock(data.emittedAt);
    }
    if (typeof data.changedAt === 'number' && data.changedAt > 0) {
      liveStore.setStepTiming(itemId, {
        index: data.index,
        enteredAt: data.changedAt,
        ...(typeof data.holdUntil === 'number' && data.holdUntil > data.changedAt ? { holdUntil: data.holdUntil } : {}),
        videoStartedAt: data.videoStartedAt ?? {},
      });
    } else if (liveStore.stepTimingByItemId[itemId]?.index !== data.index) {
      liveStore.clearStepTiming(itemId);
    }
  }

  if (options?.entityRef && options?.queryClient) {
    const playedItemId = options.fallbackItemIds?.[0];
    // Keep entity-level status for the on-air item only.
    if (!dataItemId || !playedItemId || dataItemId === playedItemId) {
      options.queryClient.setQueryData([GET_SEQUENCE_INDEX_STATUS_QUERY_KEY, options.entityRef], {
        index: data.index,
        frameCount: data.frameCount,
        mediaId: data.mediaId,
      });
    }
  }
}
